/**
 * @Author Valon.Ye
 * @Date 2022/06/15 15:54:54
 * @FilePath \commerce\src\models\user.ts
 * @Description
 */
import { useCallback, useMemo } from 'react'
import { useFetch } from '@/utils/hooks'
import { UserProfileControllerApiParamCreator } from '@/api-auto-gen/organization'
import type { UserProfileResponse } from '@/api-auto-gen/organization'

import StorageMap from '@/utils/storageMap'
import { useLocalStorageState } from 'ahooks'
import { redirectMessaging, redirectCommerce, redirectToCrm, toLogin } from '@/utils'

import { pushToCommerce } from '@/utils/redirect'
import { AppReferEnum } from '@/utils/hooks/useAppReference'

type IUserProfile = UserProfileResponse & {
  currencyChart: string
}

export default function useUser() {
  const [, setLocalOrgId] = useLocalStorageState<string>(StorageMap.ORG_ID)
  const [localUserInfo, setLocalUserInfo] = useLocalStorageState<IUserProfile>(StorageMap.USER_INFO)
  const [appRefer] = useLocalStorageState<AppReferEnum>(StorageMap.APP_REFER)

  const getUserInfo = useFetch<
    BasicResponse<IUserProfile>,
    [],
    IUserProfile | undefined,
    IUserProfile
  >(UserProfileControllerApiParamCreator.getProfileUsingGET, {
    manual: true,
    formatResult(res) {
      if (!res?.data) return undefined
      return {
        ...res.data,
        currencyChart: '¥',
      }
    },
    onSuccess(res) {
      let shouldReloadApp = false
      if (localUserInfo?.role_id && res?.role_id && localUserInfo.role_id !== res.role_id) {
        // 检测权限是否正确
        shouldReloadApp = true
      }
      try {
        setLocalOrgId(res?.user_org_id)
        setLocalUserInfo(res)
      } catch (err) {
        console.log(err)
      }
      if (shouldReloadApp) {
        // 获取用户信息前后权限不匹配，重载应用
        window.location.reload()
      }
    },
    onError() {},
  })

  const isCommerceEnabled = useMemo(() => {
    const { asset_ssc_enabled, asset_cnde_enabled } = getUserInfo.data || {}
    return Boolean(asset_cnde_enabled || asset_ssc_enabled)
  }, [getUserInfo.data])

  const isMessagingEnabled = useMemo(() => {
    const { asset_cnde_enabled, asset_ssm_enabled } = getUserInfo.data || {}
    return Boolean(asset_cnde_enabled || asset_ssm_enabled)
  }, [getUserInfo.data])

  const isCrmEnabled = useMemo(() => {
    const { asset_cnde_enabled, asset_crm_enabled } = getUserInfo.data || {}
    return Boolean(asset_cnde_enabled || asset_crm_enabled)
  }, [getUserInfo.data])

  const isAppHasAvailableAsset = useMemo(() => {
    const { asset_cnde_enabled, asset_ssc_enabled, asset_ssm_enabled, asset_crm_enabled } =
      getUserInfo.data || {}
    return Boolean(
      asset_cnde_enabled || asset_ssc_enabled || asset_ssm_enabled || asset_crm_enabled,
    )
  }, [getUserInfo.data])

  const clearUserInfo = useCallback(() => {
    setLocalUserInfo({} as IUserProfile)
    setLocalOrgId(undefined)
  }, [setLocalOrgId, setLocalUserInfo])

  const refreshUserInfo = useCallback(() => {
    // clearUserInfo()
    getUserInfo.run()
  }, [getUserInfo])

  const setupApp = useCallback(() => {
    return new Promise<boolean>((resolve, reject) =>
      getUserInfo
        .run()
        .then((data) => {
          if (!data?.user_id || !data.user_org_id) {
            toLogin()
            return reject(new Error('auth info expired'))
          }
          const _isCommerceEnabled = data?.asset_cnde_enabled || data?.asset_ssc_enabled
          const _isMessagingEnabled = data?.asset_cnde_enabled || data?.asset_ssm_enabled
          const _isCrmEnabled = data?.asset_cnde_enabled || data?.asset_crm_enabled
          // 如果两个权限都有，那么根据appRefer判断跳转到哪个应用，否则跳转到有权限的应用
          if (_isCommerceEnabled && _isMessagingEnabled) {
            resolve(true)
            if (appRefer === AppReferEnum.SSC) {
              return setTimeout(redirectCommerce)
            }
            if (appRefer === AppReferEnum.SSM) {
              return setTimeout(redirectMessaging)
            }
          }
          if (_isCommerceEnabled) {
            resolve(true)
            return setTimeout(redirectCommerce)
          }
          if (_isMessagingEnabled) {
            resolve(true)
            return setTimeout(redirectMessaging)
          }
          if (_isCrmEnabled) {
            resolve(true)
            return setTimeout(redirectToCrm)
          }
          return resolve(false)
        })
        .catch((err) => {
          if (err) toLogin()
          reject(err || new Error('auth info expired'))
        }),
    )
  }, [getUserInfo, appRefer])

  /**
   * @author: Valon.Ye
   * @description: 检查app的asset状态如果均没有就去idaas重新登陆
   * @return {*}
   */
  const checkApp = useCallback(
    () =>
      new Promise<boolean>((resolve, reject) =>
        getUserInfo
          .run()
          .then((data) => {
            if (!data?.user_id || !data.user_org_id) {
              toLogin()
              return reject()
            }
            const _isCommerceEnabled = data?.asset_cnde_enabled || data?.asset_ssc_enabled
            const _isMessagingEnabled = data?.asset_cnde_enabled || data?.asset_ssm_enabled
            const _isCrmEnabled = data?.asset_cnde_enabled || data?.asset_crm_enabled

            if (_isCommerceEnabled) {
              resolve(true)
              return pushToCommerce()
            }
            if (_isMessagingEnabled) {
              resolve(true)
              return setTimeout(redirectMessaging)
            }
            if (_isCrmEnabled) {
              resolve(true)
              return setTimeout(redirectToCrm)
            }
            toLogin()
            return reject(new Error('auth info expired'))
          })
          .catch((err) => {
            if (err) toLogin()
            reject(err || new Error('auth info expired'))
          }),
      ),
    [getUserInfo],
  )

  return {
    isAppHasAvailableAsset,
    isCommerceEnabled,
    isMessagingEnabled,
    isCrmEnabled,
    userInfo: getUserInfo.data || localUserInfo,
    remoteUserInfo: getUserInfo.data,
    refreshUserInfo,
    clearUserInfo,
    userInfoError: getUserInfo.error,
    userInfoLoading: getUserInfo.loading,
    fetchProfile: getUserInfo.run,
    setupApp,
    checkApp,
  }
}
